// src/pages/vendor/Profile.jsx - NO BLINKING VERSION
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Badge,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Divider,
  Radio,
  RadioGroup,
  Collapse,
  Snackbar,
} from "@mui/material";
import {
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Store,
  Phone,
  Star,
  ShoppingBag,
  Verified,
  Warning,
  AccountBalanceWallet,
  Payment,
  CheckCircle,
  Business,
  PhoneAndroid,
  AccountBalance as BankIcon,
  Close,
  Refresh,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { vendorApi } from "../../api/vendorApi";
import { getUserAvatarUrl } from "../../utils/imageUtils";

// Ethiopian Banks List
const ETHIOPIAN_BANKS = [
  { code: "CBET", name: "Commercial Bank of Ethiopia" },
  { code: "BOA", name: "Bank of Abyssinia" },
  { code: "AWASH", name: "Awash Bank" },
  { code: "DASHEN", name: "Dashen Bank" },
  { code: "WEGAGEN", name: "Wegagen Bank" },
  { code: "UNITED", name: "United Bank" },
  { code: "NIB", name: "NIB International Bank" },
  { code: "ZEMEN", name: "Zemen Bank" },
  { code: "OROMIA", name: "Oromia Bank" },
  { code: "BERHAN", name: "Berhan Bank" },
  { code: "ABAY", name: "Abay Bank" },
  { code: "AMHARA", name: "Amhara Bank" },
  { code: "ADDIS", name: "Addis International Bank" },
  { code: "DEBUB", name: "Debub Global Bank" },
  { code: "BUNNA", name: "Bunna Bank" },
  { code: "ENAT", name: "Enat Bank" },
  { code: "TSEDEY", name: "Tsedey Bank" },
  { code: "SIINQEE", name: "Siinqee Bank" },
  { code: "GOH", name: "Goh Betoch Bank" },
];

// Payout Payment Methods
const PAYOUT_METHODS = [
  { 
    id: 'bank_transfer', 
    name: 'Bank Transfer', 
    icon: <BankIcon />, 
    description: 'Receive payout via bank transfer',
    requiresBankDetails: true,
    requiresPhone: false,
  },
  { 
    id: 'telebirr', 
    name: 'TeleBirr', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via TeleBirr mobile money',
    requiresBankDetails: false,
    requiresPhone: true,
  },
  { 
    id: 'mpesa', 
    name: 'M-Pesa', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via M-Pesa mobile money',
    requiresBankDetails: false,
    requiresPhone: true,
  },
  { 
    id: 'cbebirr', 
    name: 'CBE Birr', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via CBE Birr digital payment',
    requiresBankDetails: false,
    requiresPhone: true,
  },
  { 
    id: 'awashbirr', 
    name: 'Awash Birr', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via Awash Birr wallet',
    requiresBankDetails: false,
    requiresPhone: true,
  },
  { 
    id: 'ebirr', 
    name: 'eBirr', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via eBirr digital payment',
    requiresBankDetails: false,
    requiresPhone: true,
  },
  { 
    id: 'yaya', 
    name: 'Yaya Wallet', 
    icon: <PhoneAndroid />, 
    description: 'Receive payout via Yaya Wallet',
    requiresBankDetails: false,
    requiresPhone: true,
  },
  { 
    id: 'coopay', 
    name: 'Coopay', 
    icon: <Business />, 
    description: 'Receive payout via Coopay eBirr',
    requiresBankDetails: true,
    requiresPhone: false,
  },
  { 
    id: 'enat_bank', 
    name: 'Enat Bank', 
    icon: <Business />, 
    description: 'Receive payout via Enat Bank transfer',
    requiresBankDetails: true,
    requiresPhone: false,
  },
];

const getPayoutMethodInfo = (methodId) => {
  return PAYOUT_METHODS.find(m => m.id === methodId) || PAYOUT_METHODS[0];
};

const getPayoutMethodName = (methodId) => {
  const method = getPayoutMethodInfo(methodId);
  return method ? method.name : "Bank Transfer";
};

const VendorProfile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { user, updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(true);
  
  const [profileData, setProfileData] = useState({
    storeName: "",
    storeDescription: "",
    businessEmail: "",
    businessPhone: "",
    businessAddress: "",
    bankAccountName: "",
    bankAccountNumber: "",
    bankName: "",
    payoutMethod: "bank_transfer",
    payoutPhoneNumber: "",
  });

  // ✅ Use ref to prevent multiple refreshes
  const initialLoadDone = useRef(false);

  const userAvatarUrl = getUserAvatarUrl(user);
  const displayAvatarUrl = previewImage || userAvatarUrl;

  // ============================================================
  // REFRESH USER DATA FROM API - NO BLINKING
  // ============================================================
  const refreshUserData = useCallback(async (showSuccess = false) => {
    // ✅ Don't show loading spinner for background refresh
    if (showSuccess) {
      setRefreshing(true);
    }
    
    try {
      console.log('[refreshUserData] Fetching latest vendor data...');
      const response = await vendorApi.getProfile();
      
      if (response?.success && response?.vendor) {
        const vendor = response.vendor;
        console.log('[refreshUserData] Received vendor data:', vendor);
        
        // ✅ Update the user context - this triggers re-render
        updateUser(vendor);
        
        // ✅ Update local state without causing extra re-renders
        setProfileData({
          storeName: vendor.storeName || '',
          storeDescription: vendor.storeDescription || '',
          businessEmail: vendor.businessEmail || '',
          businessPhone: vendor.businessPhone || '',
          businessAddress: vendor.businessAddress || '',
          bankAccountName: vendor.bankAccountName || '',
          bankAccountNumber: vendor.bankAccountNumber || '',
          bankName: vendor.bankName || '',
          payoutMethod: vendor.payoutMethod || 'bank_transfer',
          payoutPhoneNumber: vendor.payoutPhoneNumber || '',
        });

        // Check if selected method requires bank details
        const method = PAYOUT_METHODS.find(m => m.id === (vendor.payoutMethod || 'bank_transfer'));
        setShowBankDetails(method ? method.requiresBankDetails : true);
        
        if (showSuccess) {
          setSuccess('Profile data refreshed successfully!');
          setTimeout(() => setSuccess(null), 3000);
        }
        
        return vendor;
      }
      return null;
    } catch (error) {
      console.error('[refreshUserData] Failed to refresh:', error);
      if (showSuccess) {
        setError('Failed to refresh profile data');
        setTimeout(() => setError(null), 3000);
      }
      return null;
    } finally {
      if (showSuccess) {
        setRefreshing(false);
      }
    }
  }, [updateUser]);

  // ============================================================
  // INITIALIZE DATA FROM USER - Only when user changes
  // ============================================================
  useEffect(() => {
    if (user) {
      console.log('[Profile] User data from context:', user);
      setProfileData({
        storeName: user.storeName || "",
        storeDescription: user.storeDescription || "",
        businessEmail: user.businessEmail || user.email || "",
        businessPhone: user.businessPhone || "",
        businessAddress: user.businessAddress || "",
        bankAccountName: user.bankAccountName || "",
        bankAccountNumber: user.bankAccountNumber || "",
        bankName: user.bankName || "",
        payoutMethod: user.payoutMethod || "bank_transfer",
        payoutPhoneNumber: user.payoutPhoneNumber || "",
      });

      const method = PAYOUT_METHODS.find(m => m.id === (user.payoutMethod || "bank_transfer"));
      setShowBankDetails(method ? method.requiresBankDetails : true);
    }
  }, [user]);

  // ============================================================
  // REFRESH ON MOUNT - Only once
  // ============================================================
  useEffect(() => {
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      // ✅ Silent refresh on mount (no loading indicator)
      refreshUserData(false);
    }
  }, []); // Empty dependency array - only runs once

  // ============================================================
  // HANDLERS
  // ============================================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value || '',
    }));
  };

  const handlePayoutMethodChange = (e) => {
    const methodId = e.target.value;
    const method = PAYOUT_METHODS.find(m => m.id === methodId);
    
    setProfileData(prev => ({
      ...prev,
      payoutMethod: methodId,
      payoutPhoneNumber: method?.requiresPhone ? prev.payoutPhoneNumber : '',
      bankAccountName: method?.requiresBank ? prev.bankAccountName : '',
      bankAccountNumber: method?.requiresBank ? prev.bankAccountNumber : '',
      bankName: method?.requiresBank ? prev.bankName : '',
    }));

    if (method) {
      setShowBankDetails(method.requiresBankDetails);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('storeLogo', selectedFile);
      
      const response = await vendorApi.updateStoreLogo(formData);
      if (response && response.vendor) {
        // ✅ Update user context with new logo
        updateUser(response.vendor);
        // ✅ Refresh data without page reload
        await refreshUserData(true);
      }
      setOpenImageDialog(false);
      setSelectedFile(null);
      setPreviewImage(null);
      setSuccess('Store logo updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to upload logo');
      setTimeout(() => setError(null), 3000);
    } finally {
      setUploading(false);
    }
  };

  // ============================================================
  // HANDLE SAVE - NO PAGE RELOAD, NO BLINKING
  // ============================================================
  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const saveData = {
        storeName: profileData.storeName?.trim() || '',
        storeDescription: profileData.storeDescription || '',
        businessEmail: profileData.businessEmail?.trim() || '',
        businessPhone: profileData.businessPhone?.trim() || '',
        businessAddress: profileData.businessAddress || '',
        bankAccountName: profileData.bankAccountName?.trim() || '',
        bankAccountNumber: profileData.bankAccountNumber?.trim() || '',
        bankName: profileData.bankName || '',
        payoutMethod: profileData.payoutMethod || 'bank_transfer',
        payoutPhoneNumber: profileData.payoutPhoneNumber?.trim() || '',
      };

      console.log('[handleSave] Sending data:', saveData);

      const response = await vendorApi.updateProfile(saveData);

      console.log('[handleSave] Response:', response);

      if (!response?.success || !response?.vendor) {
        throw new Error(response?.message || 'Failed to update profile');
      }

      const updatedVendor = response.vendor;
      console.log('[handleSave] Updated vendor:', updatedVendor);

      // ✅ Update the user context with fresh data - this triggers re-render
      updateUser(updatedVendor);

      // ✅ Update local state with the new data
      setProfileData({
        storeName: updatedVendor.storeName || '',
        storeDescription: updatedVendor.storeDescription || '',
        businessEmail: updatedVendor.businessEmail || '',
        businessPhone: updatedVendor.businessPhone || '',
        businessAddress: updatedVendor.businessAddress || '',
        bankAccountName: updatedVendor.bankAccountName || '',
        bankAccountNumber: updatedVendor.bankAccountNumber || '',
        bankName: updatedVendor.bankName || '',
        payoutMethod: updatedVendor.payoutMethod || 'bank_transfer',
        payoutPhoneNumber: updatedVendor.payoutPhoneNumber || '',
      });

      // ✅ Silent refresh from API (no loading indicator)
      await refreshUserData(false);

      // ✅ Exit edit mode
      setEditing(false);
      
      // ✅ Show success message
      setSuccess('Profile and payout details saved successfully!');
      setTimeout(() => setSuccess(null), 3000);

    } catch (error) {
      console.error('[handleSave] Error:', error);
      setError(error.message || 'Failed to save profile');
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to current user data
    setProfileData({
      storeName: user?.storeName || "",
      storeDescription: user?.storeDescription || "",
      businessEmail: user?.businessEmail || user?.email || "",
      businessPhone: user?.businessPhone || "",
      businessAddress: user?.businessAddress || "",
      bankAccountName: user?.bankAccountName || "",
      bankAccountNumber: user?.bankAccountNumber || "",
      bankName: user?.bankName || "",
      payoutMethod: user?.payoutMethod || "bank_transfer",
      payoutPhoneNumber: user?.payoutPhoneNumber || "",
    });
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const getAvatarLetter = () => {
    if (user?.storeName) return user.storeName[0].toUpperCase();
    return 'S';
  };

  const getDisplayBankName = () => {
    const bankName = user?.bankName || "";
    return bankName || "Not set";
  };

  const getSelectedMethod = () => {
    return getPayoutMethodInfo(profileData.payoutMethod);
  };

  const getSelectedMethodRequiresBank = () => {
    const method = getSelectedMethod();
    return method ? method.requiresBankDetails : true;
  };

  const getSelectedMethodRequiresPhone = () => {
    const method = getSelectedMethod();
    return method ? method.requiresPhone : false;
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedMethod = getSelectedMethod();

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Store Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your store information and payout preferences
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: '100%', sm: 'auto' }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refreshUserData(true)}
              disabled={refreshing}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
              sx={{ flex: isMobile ? 1 : 'none' }}
            >
              {refreshing ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
            {editing ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleCancel}
                  disabled={loading}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={loading}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  {loading ? <CircularProgress size={20} /> : "Save Changes"}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => setEditing(true)}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
              >
                Edit Profile
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Alerts - Auto dismiss after 3 seconds */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2, borderRadius: 2 }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert 
          severity="success" 
          sx={{ mb: 2, borderRadius: 2 }} 
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Profile Header Card */}
      <Paper sx={{ 
        p: { xs: 2, sm: 3 }, 
        mb: 3, 
        borderRadius: 3, 
        textAlign: "center",
      }}>
        <Stack alignItems="center" spacing={2}>
          <Box sx={{ position: "relative" }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <IconButton
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    width: { xs: 32, sm: 40 },
                    height: { xs: 32, sm: 40 },
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                  onClick={() => setOpenImageDialog(true)}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <PhotoCamera sx={{ fontSize: { xs: 16, sm: 20 } }} />
                </IconButton>
              }
            >
              <Avatar
                sx={{
                  width: { xs: 100, sm: 120 },
                  height: { xs: 100, sm: 120 },
                  bgcolor: displayAvatarUrl ? 'transparent' : 'primary.main',
                  fontSize: { xs: 36, sm: 48 },
                  border: '3px solid',
                  borderColor: 'background.paper',
                  boxShadow: theme.shadows[3],
                }}
                src={displayAvatarUrl}
              >
                {!displayAvatarUrl && getAvatarLetter()}
              </Avatar>
            </Badge>
          </Box>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
            {user.storeName || "Store Name"}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <Chip icon={<Star sx={{ color: "#ffc107" }} />} label={`${user.rating || 0} Rating`} />
            <Chip icon={<ShoppingBag />} label={`${user.totalSales || 0} Sales`} />
            {user.isVerified && <Chip icon={<Verified />} label="Verified" color="success" />}
            {!user.isVerified && <Chip icon={<Warning />} label="Pending Verification" color="warning" variant="outlined" />}
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Store Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                <Store sx={{ mr: 1, verticalAlign: 'middle' }} />
                Store Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Store Name
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="storeName"
                      value={profileData.storeName}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1" fontWeight="500">
                      {user.storeName || "Not set"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Description
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="storeDescription"
                      value={profileData.storeDescription}
                      onChange={handleChange}
                      multiline
                      rows={3}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {user.storeDescription || "No description provided"}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
                Contact Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Business Email
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="businessEmail"
                      value={profileData.businessEmail}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {user.businessEmail || user.email || "Not set"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Phone
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="businessPhone"
                      value={profileData.businessPhone}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {user.businessPhone || "Not set"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Address
                  </Typography>
                  {editing ? (
                    <TextField
                      fullWidth
                      name="businessAddress"
                      value={profileData.businessAddress}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  ) : (
                    <Typography variant="body1">
                      {user.businessAddress || "Not set"}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Payout Payment Method */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                <Payment sx={{ mr: 1, verticalAlign: "middle" }} />
                Payout Preferences
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Select how you want to receive your payouts when admin approves your withdrawal request
              </Typography>

              {editing ? (
                <>
                  <RadioGroup
                    name="payoutMethod"
                    value={profileData.payoutMethod}
                    onChange={handlePayoutMethodChange}
                  >
                    <Grid container spacing={1}>
                      {PAYOUT_METHODS.map((method) => (
                        <Grid item xs={12} sm={6} md={4} key={method.id}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderColor: profileData.payoutMethod === method.id ? 'primary.main' : 'grey.300',
                              borderWidth: profileData.payoutMethod === method.id ? 2 : 1,
                              bgcolor: profileData.payoutMethod === method.id ? 'primary.light' : 'transparent',
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: profileData.payoutMethod === method.id ? 'primary.light' : 'grey.50',
                              },
                              transition: 'all 0.2s ease',
                            }}
                            onClick={() => {
                              setProfileData(prev => ({ ...prev, payoutMethod: method.id }));
                              setShowBankDetails(method.requiresBankDetails);
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <Box sx={{ color: profileData.payoutMethod === method.id ? 'primary.main' : 'text.secondary' }}>
                                {method.icon}
                              </Box>
                              <Box flex={1}>
                                <Typography variant="body2" fontWeight={500}>
                                  {method.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {method.description}
                                </Typography>
                              </Box>
                              <Radio
                                checked={profileData.payoutMethod === method.id}
                                onChange={() => {}}
                                value={method.id}
                                name="payoutMethod"
                                size="small"
                              />
                            </Stack>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </RadioGroup>

                  {/* Conditional Phone Number Field */}
                  <Collapse in={getSelectedMethodRequiresPhone()}>
                    <TextField
                      fullWidth
                      name="payoutPhoneNumber"
                      label="Mobile Number for Payout"
                      value={profileData.payoutPhoneNumber}
                      onChange={handleChange}
                      size="small"
                      sx={{ mt: 2 }}
                      placeholder="e.g., 0912345678"
                      helperText="Required for mobile money payouts (TeleBirr, M-Pesa, CBE Birr, etc.)"
                    />
                  </Collapse>

                  {/* Bank Details - Only shown when needed */}
                  <Collapse in={showBankDetails}>
                    <Box sx={{ mt: 3 }}>
                      <Divider sx={{ mb: 2 }}>
                        <Chip label="Bank Details" size="small" />
                      </Divider>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                        Bank details required for {getPayoutMethodName(profileData.payoutMethod)} payouts
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            name="bankAccountName"
                            label="Account Holder Name"
                            value={profileData.bankAccountName}
                            onChange={handleChange}
                            size="small"
                            placeholder="e.g., John Doe"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            name="bankAccountNumber"
                            label="Account Number"
                            value={profileData.bankAccountNumber}
                            onChange={handleChange}
                            size="small"
                            placeholder="e.g., 1000123456789"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Bank Name</InputLabel>
                            <Select
                              name="bankName"
                              value={profileData.bankName}
                              onChange={handleChange}
                              label="Bank Name"
                            >
                              <MenuItem value="">
                                <em>Select Bank</em>
                              </MenuItem>
                              {profileData.bankName &&
                                !ETHIOPIAN_BANKS.some((bank) => bank.name === profileData.bankName) && (
                                  <MenuItem value={profileData.bankName}>
                                    {profileData.bankName}
                                  </MenuItem>
                                )}
                              {ETHIOPIAN_BANKS.map((bank) => (
                                <MenuItem key={bank.code} value={bank.name}>
                                  {bank.name}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>Select your bank from the list</FormHelperText>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </>
              ) : (
                <Stack spacing={2}>
                  {/* Selected Method Display */}
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: 'success.light',
                      borderColor: 'success.main',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box sx={{ fontSize: 32 }}>
                        {selectedMethod?.icon || <BankIcon />}
                      </Box>
                      <Box flex={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {getPayoutMethodName(profileData.payoutMethod)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedMethod?.description || 'Default payout method'}
                        </Typography>
                        {user.payoutPhoneNumber && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Phone: {user.payoutPhoneNumber}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        icon={<CheckCircle />}
                        label="Selected"
                        color="success"
                        size="small"
                        sx={{ ml: 'auto' }}
                      />
                    </Stack>
                  </Paper>

                  {/* Show Bank Details if selected method requires it */}
                  {getSelectedMethodRequiresBank() && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        <AccountBalanceWallet sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                        Bank Details
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Account Holder</Typography>
                          <Typography variant="body2">{user.bankAccountName || "Not set"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Account Number</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {user.bankAccountNumber || "Not set"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Bank Name</Typography>
                          <Typography variant="body2">{getDisplayBankName()}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  {/* Show Phone Number if selected method requires it */}
                  {getSelectedMethodRequiresPhone() && user.payoutPhoneNumber && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        <PhoneAndroid sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                        Mobile Number
                      </Typography>
                      <Typography variant="body2">{user.payoutPhoneNumber}</Typography>
                    </Paper>
                  )}

                  {/* Show selected method details in a table */}
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                      Current Payout Settings
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary">Payout Method</Typography>
                        <Typography variant="body2" fontWeight="500">
                          {getPayoutMethodName(user.payoutMethod || 'bank_transfer')}
                        </Typography>
                      </Grid>
                      {user.payoutPhoneNumber && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                          <Typography variant="body2" fontWeight="500">
                            {user.payoutPhoneNumber}
                          </Typography>
                        </Grid>
                      )}
                      {user.bankName && getSelectedMethodRequiresBank() && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Bank</Typography>
                          <Typography variant="body2" fontWeight="500">
                            {getDisplayBankName()}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Store Statistics */}
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Store Statistics
            </Typography>
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.totalProducts || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Products</Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.totalOrders || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Orders</Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.totalCustomers || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Customers</Typography>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack alignItems="center">
                  <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" color="primary">
                    {user.commissionRate || 0}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Commission Rate</Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Update Logo Dialog */}
      <Dialog 
        open={openImageDialog} 
        onClose={() => {
          setOpenImageDialog(false);
          setSelectedFile(null);
          setPreviewImage(null);
        }} 
        maxWidth="xs" 
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Update Store Logo</Typography>
            <IconButton onClick={() => {
              setOpenImageDialog(false);
              setSelectedFile(null);
              setPreviewImage(null);
            }}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Avatar
              sx={{
                width: { xs: 100, sm: 120 },
                height: { xs: 100, sm: 120 },
                mx: 'auto',
                mb: 2,
                bgcolor: 'primary.main',
              }}
              src={displayAvatarUrl}
            >
              {!displayAvatarUrl && getAvatarLetter()}
            </Avatar>
            <Button variant="outlined" component="label" sx={{ mt: 2 }}>
              Choose New Logo
              <input type="file" hidden accept="image/*" onChange={handleFileSelect} />
            </Button>
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => {
              setOpenImageDialog(false);
              setSelectedFile(null);
              setPreviewImage(null);
            }} 
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadLogo}
            disabled={!selectedFile || uploading}
            fullWidth={isMobile}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorProfile;